// features/profile/edit/LinksCard.tsx — Edit Profile → Links (1.27.70).
//
// Up to PROFILE_LIMITS.links links, shown on the profile in this order. Reorder by
// dragging a row, or (hand-rolled accessibility) by focusing its handle and using
// the arrow keys; every move is announced. Only http(s) addresses are accepted,
// here and again on the server (profile_fields.py).
import { useState, type DragEvent, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { PROFILE_LIMITS, type ProfileLink } from '@/core/models/account';
import { isWebAddress, linkIcon, linkText, normalizeUrl } from '@/core/lib/profileFields';
import { CardHead, DashCard } from '@/features/settings/dashboard/DashCard';
import type { DraftErrors, ProfileDraftState } from './useProfileDraft';
import { FormField } from './fields';
import { TextInput } from './fieldStyles';

const List = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`;

const Item = styled.li<{ $dragging: boolean; $over: boolean }>`
  display: grid;
  grid-template-columns: auto 20px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 10px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  opacity: ${({ $dragging }) => ($dragging ? 0.5 : 1)};
  background: ${({ theme, $over }) => ($over ? theme.color.hover : 'transparent')};
  > svg {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Handle = styled.button`
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 32px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: grab;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  > span:first-child {
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.color.textPrimary};
  }
  > span:last-child {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.color.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 2px;
`;

const IconAction = styled.button<{ $danger?: boolean }>`
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme, $danger }) => ($danger ? theme.color.danger : theme.color.textMuted)};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
  }
`;

const Editor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surfaceRaised};
`;

const EditorButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const AddButton = styled(Button)`
  width: 100%;
  margin-top: 12px;
`;

const Foot = styled.p<{ $error?: boolean }>`
  margin: 10px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme, $error }) => ($error ? theme.color.danger : theme.color.textMuted)};
`;

const Live = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

interface EditState {
  /** Index being edited, or null when adding a new link. */
  index: number | null;
  label: string;
  href: string;
  error: string | null;
}

function move<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

export function LinksCard({ state, errors }: { state: ProfileDraftState; errors: DraftErrors }) {
  const { draft, set } = state;
  const links = draft.links;
  const [edit, setEdit] = useState<EditState | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [announce, setAnnounce] = useState('');
  const full = links.length >= PROFILE_LIMITS.links;

  function reorder(from: number, to: number) {
    if (to < 0 || to >= links.length || from === to) return;
    const next = move(links, from, to);
    set('links', next);
    const moved = next[to];
    if (moved) setAnnounce(`Moved ${linkText(moved)} to position ${to + 1} of ${next.length}`);
  }

  function onHandleKey(i: number, e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const to = e.key === 'ArrowUp' ? i - 1 : i + 1;
    reorder(i, to);
    // Keep focus on the handle of the link that moved.
    requestAnimationFrame(() =>
      document.querySelector<HTMLButtonElement>(`[data-link-handle="${to}"]`)?.focus(),
    );
  }

  function onDrop(i: number, e: DragEvent) {
    e.preventDefault();
    if (dragFrom !== null) reorder(dragFrom, i);
    setDragFrom(null);
    setDragOver(null);
  }

  function saveEdit() {
    if (!edit) return;
    const href = normalizeUrl(edit.href);
    if (!isWebAddress(href)) {
      setEdit({ ...edit, error: 'Enter a web address, like example.org.' });
      return;
    }
    const link: ProfileLink = { label: edit.label.trim(), href };
    set(
      'links',
      edit.index === null ? [...links, link] : links.map((l, i) => (i === edit.index ? link : l)),
    );
    setEdit(null);
  }

  return (
    <DashCard labelledBy="ep-links-title" id="links" testId="links-card">
      <CardHead
        icon="link"
        title="Links"
        titleId="ep-links-title"
        subtitle={`Add up to ${PROFILE_LIMITS.links} links to your other sites.`}
      />
      <Live role="status" aria-live="polite">
        {announce}
      </Live>
      {links.length > 0 && (
        <List aria-label="Your links">
          {links.map((l, i) => (
            <Item
              key={`${i}-${l.href}`}
              draggable
              $dragging={dragFrom === i}
              $over={dragOver === i && dragFrom !== i}
              onDragStart={(e) => {
                setDragFrom(i);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(i);
              }}
              onDragLeave={() => setDragOver((o) => (o === i ? null : o))}
              onDrop={(e) => onDrop(i, e)}
              onDragEnd={() => {
                setDragFrom(null);
                setDragOver(null);
              }}
              data-testid={`link-row-${i}`}
            >
              <Handle
                type="button"
                data-link-handle={i}
                aria-label={`Reorder ${linkText(l)}, position ${i + 1} of ${links.length}. Use the up and down arrow keys.`}
                onKeyDown={(e) => onHandleKey(i, e)}
              >
                <Icon name="grip-vertical" size={16} />
              </Handle>
              <Icon name={linkIcon(l.href)} size={18} />
              <Text>
                <span>{linkText(l)}</span>
                <span>{l.href}</span>
              </Text>
              <RowActions>
                <IconAction
                  type="button"
                  aria-label={`Edit ${linkText(l)}`}
                  onClick={() => setEdit({ index: i, label: l.label, href: l.href, error: null })}
                >
                  <Icon name="edit" size={15} />
                </IconAction>
                <IconAction
                  type="button"
                  $danger
                  aria-label={`Remove ${linkText(l)}`}
                  onClick={() => {
                    set(
                      'links',
                      links.filter((_, j) => j !== i),
                    );
                    setAnnounce(`Removed ${linkText(l)}`);
                  }}
                >
                  <Icon name="trash" size={15} />
                </IconAction>
              </RowActions>
            </Item>
          ))}
        </List>
      )}

      {edit ? (
        <Editor role="group" aria-label={edit.index === null ? 'Add a link' : 'Edit link'}>
          <FormField label="Name (optional)" hint="Leave empty to show the site's name.">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                value={edit.label}
                maxLength={PROFILE_LIMITS.linkLabel}
                placeholder="YouTube"
                onChange={(e) => setEdit({ ...edit, label: e.target.value })}
              />
            )}
          </FormField>
          <FormField label="Web address" error={edit.error ?? undefined}>
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                $invalid={invalid}
                inputMode="url"
                autoFocus
                value={edit.href}
                maxLength={PROFILE_LIMITS.linkHref}
                placeholder="youtube.com/@you"
                onChange={(e) => setEdit({ ...edit, href: e.target.value, error: null })}
                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
              />
            )}
          </FormField>
          <EditorButtons>
            <Button variant="ghost" size="sm" onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={saveEdit}>
              {edit.index === null ? 'Add link' : 'Save link'}
            </Button>
          </EditorButtons>
        </Editor>
      ) : (
        <AddButton
          variant="secondary"
          disabled={full}
          onClick={() => setEdit({ index: null, label: '', href: '', error: null })}
        >
          <Icon name="plus" size={14} />
          {full ? `You have ${PROFILE_LIMITS.links} links` : 'Add link'}
        </AddButton>
      )}
      <Foot $error={!!errors.links}>
        {errors.links ??
          'Drag to reorder, or use the arrow keys on a handle. Links appear on your profile.'}
      </Foot>
    </DashCard>
  );
}
